/**
 * One list of everything a job has on paper.
 *
 * Jonathon, 2026-08-28: "basically any pdf uploaded anywhere in the customer's
 * job needs to go into docs."
 *
 * Docs listed `proposals` and nothing else — what was presented, opened and
 * signed. Everything else a job accumulates lived wherever the screen that made
 * it put it, so "what have we got on this job?" meant knowing which card to
 * open. This merges the two sources into the order somebody reads them in.
 *
 * ## Why three sources rather than one table
 *
 * A presented document is not a file. It is a live thing with a token, a state
 * and a customer on the other end of it: it can be opened, signed, and voided,
 * and voiding it stands an estimate back down (ADR 0047). Copying it into a
 * file register would either duplicate that state or lose it.
 *
 * A change order is the same kind of thing on its own table and its own page,
 * `/c/<token>`, because it is a different document with a different life. It
 * was missing from here until 2026-09-23, so the one document on a job that
 * changes the contract after it is signed was the one document Docs did not
 * list (ADR 0205).
 *
 * A file is the opposite — bytes that do not change, in a bucket, that some
 * screen put there. `job_files` records where they are and what happened to
 * them.
 *
 * So Docs reads all three and sorts by date. What the reader wants is one
 * list; what the database wants is three tables; this function is the whole of
 * the difference between them.
 */

/** A presented document, as `fetchJobDocuments` returns it. */
export interface ProposalEntry {
  id: string;
  title: string | null;
  status: string;
  token: string;
  revision: number;
  sent_at: string | null;
  /** When the customer's link stops opening. Null until it is sent. */
  expires_at: string | null;
  created_at: string;
  accepted_at: string | null;
  signer_name: string | null;
  /**
   * When the customer was emailed their copy of the signed agreement, and why
   * it did not go if it did not (ADR 0122).
   *
   * Optional because a caller assembled before 2026-09-09 does not select
   * them, and a document nobody has signed has neither.
   */
  signed_copy_sent_at?: string | null;
  signed_copy_error?: string | null;
}

/**
 * A change order presented to the customer, as `change_order_documents` holds
 * it.
 *
 * A third table rather than a second kind of proposal, because it is one:
 * `/c/<token>` is its own page with its own snapshot and its own acceptance
 * row. It was left out of this list when it was built, so a signed change
 * order — a thing a customer put their name to, that moves what they owe —
 * appeared in Docs nowhere at all. Jonathon, 2026-09-23, looking for Brian
 * Garrett's: "i also couldn't find his signed change order in the docs."
 *
 * It carries the same five answers a proposal does, so the table needs no new
 * columns: what it is, where it got to, when it went, when it was signed and
 * by whom.
 */
export interface ChangeOrderEntry {
  id: string;
  /** `change_order_documents.number` — "Change order 2" on the job. */
  number: number;
  status: string;
  token: string;
  sent_at: string | null;
  expires_at: string | null;
  created_at: string;
  accepted_at: string | null;
  signer_name: string | null;
  /** What the change came to, in cents. Shown beside the name. */
  change_total: number | null;
}

/**
 * A certificate of completion sent to the customer, as
 * `completion_certificates` holds it (ADR 0308).
 *
 * The same kind of thing as a change order — its own table, its own page at
 * `/cert/<token>`, its own signature row — and listed here for the same
 * reason: it is something a customer put their name to, and Docs is where
 * the office looks for it. The signed PDF the Edge Function files is a
 * separate row of its own, a `completion_certificate` file.
 */
export interface CertificateEntry {
  id: string;
  status: string;
  token: string;
  sent_at: string | null;
  created_at: string;
  /** Who it was made out to, off the frozen page. */
  homeowner: string | null;
  signed_at: string | null;
  signer_name: string | null;
  /** When the signed PDF was emailed, and why it was not if it was not. */
  copy_sent_at: string | null;
  copy_error: string | null;
}

/** A file on the job, as `job_files` holds it. */
export interface JobFileEntry {
  id: string;
  kind: string;
  title: string;
  bucket: string;
  path: string;
  mime_type: string | null;
  size_bytes: number | null;
  emailed_to: string | null;
  emailed_at: string | null;
  created_at: string;
}

export type DocEntry =
  | ({ type: "proposal"; at: string } & ProposalEntry)
  | ({ type: "change_order"; at: string } & ChangeOrderEntry)
  | ({ type: "certificate"; at: string } & CertificateEntry)
  | ({ type: "file"; at: string } & JobFileEntry);

/**
 * What each kind of file is called on the card.
 *
 * A label rather than the raw string, because `roof_report` is a column value
 * and "Measurement report" is what a roofer calls it. An unknown kind is
 * humanised rather than dropped — a screen added next month lists under its own
 * name without touching this file, which is the reason `kind` is free text.
 */
const LABELS: Record<string, string> = {
  upload: "Upload",
  roof_report: "Measurement report",
  signed_contract: "Signed contract",
  invoice: "Invoice",
  storm_report: "Storm report",
  material_order: "Material order",
  supplier_receipt: "Supplier receipt",
  // Filed from orders@ by the app (ADR 0311).
  supplier_acknowledgement: "Order acknowledgement",
  supplier_invoice: "Supplier invoice",
  supplier_credit_memo: "Credit memo",
  supplier_mail: "Supplier email",
  expense_receipt: "Expense receipt",
  work_order: "Work order",
  company_doc: "Company document",
  crew_doc: "Crew document",
  completion_certificate: "Signed certificate of completion",
};

export function documentKindLabel(kind: string): string {
  const known = LABELS[kind.trim().toLowerCase()];
  if (known) return known;
  const words = kind.trim().replace(/[_-]+/g, " ");
  if (words === "") return "Document";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The date a row is filed under.
 *
 * A document is filed under the day it went out, not the day it was made: a
 * proposal built on Monday and sent on Thursday is Thursday's, because that is
 * the date anybody looking for it remembers. Falls back to when it was created,
 * which is all an upload has.
 */
export function documentDate(
  entry: ProposalEntry | ChangeOrderEntry | CertificateEntry | JobFileEntry,
): string {
  if ("sent_at" in entry) return entry.sent_at ?? entry.created_at;
  return entry.emailed_at ?? entry.created_at;
}

/**
 * Everything on the job, newest first.
 *
 * Newest first because the question is nearly always about the most recent
 * thing — "what did we just send them?" — and the answer should be the first
 * row rather than the last.
 */
export function jobDocuments(
  proposals: readonly ProposalEntry[],
  files: readonly JobFileEntry[],
  /**
   * The change orders presented on this job (ADR 0205).
   *
   * Optional, and empty by default, so a caller written before change orders
   * were listed here goes on compiling and goes on showing what it showed.
   */
  changeOrders: readonly ChangeOrderEntry[] = [],
  /** The certificates of completion sent on this job (ADR 0308). */
  certificates: readonly CertificateEntry[] = [],
): DocEntry[] {
  const entries: DocEntry[] = [
    ...proposals.map((row): DocEntry => ({
      ...row,
      type: "proposal",
      at: documentDate(row),
    })),
    ...changeOrders.map((row): DocEntry => ({
      ...row,
      type: "change_order",
      at: documentDate(row),
    })),
    ...certificates.map((row): DocEntry => ({
      ...row,
      type: "certificate",
      at: documentDate(row),
    })),
    ...files.map((row): DocEntry => ({
      ...row,
      type: "file",
      at: documentDate(row),
    })),
  ];
  // Ties broken by id so the order is stable between two renders. Two files
  // uploaded in the same second used to swap places on every reload.
  return entries.sort((a, b) =>
    a.at === b.at ? a.id.localeCompare(b.id) : a.at < b.at ? 1 : -1,
  );
}

/**
 * What a file is called when it lands on somebody's desktop.
 *
 * Not the storage path: that carries a job id and a timestamp so two uploads of
 * "report.pdf" cannot collide, and neither belongs in the name a person reads.
 */
export function documentFilename(entry: JobFileEntry): string {
  const fromPath = entry.path.split("/").pop() ?? "";
  // The upload prefix is `<epoch>-`, added so the path is unique. Strip it and
  // nothing else — a supplier's own "1099-form.pdf" keeps its number.
  const cleaned = fromPath.replace(/^\d{10,}-/, "");
  return cleaned || `${entry.title}.pdf`;
}

/**
 * Whether this file can be deleted from the Docs card.
 *
 * Only what a person put there by hand: a document dragged onto the card, a
 * supplier's receipt uploaded on a material order, and a receipt uploaded on
 * an other expense — all somebody choosing a file, and all can be the wrong
 * file. A supplier's email filed from orders@ by the app (ADR 0311) is the
 * same kind of thing: a document that can land on the wrong order, and
 * nothing was sent or signed because of it. Everything else is a record
 * of something that happened — a report the measurements were read off, an
 * invoice a customer has, a contract somebody signed — and deleting the
 * register entry would not undo any of it, it would only make the job look like
 * it never happened.
 */
export function canDeleteDocument(entry: JobFileEntry): boolean {
  return (
    entry.kind === "upload" ||
    entry.kind === "supplier_receipt" ||
    entry.kind === "supplier_acknowledgement" ||
    entry.kind === "supplier_invoice" ||
    entry.kind === "supplier_credit_memo" ||
    entry.kind === "supplier_mail" ||
    entry.kind === "expense_receipt"
  );
}
